import React, { useCallback, useEffect, useMemo } from 'react'
import { View } from 'react-native'

import { SigningStatus } from '@ambire-common/controllers/signAccountOp/signAccountOp'
import { Key } from '@ambire-common/interfaces/keystore'
import { AccountOp } from '@ambire-common/libs/accountOp/accountOp'
import Text from '@common/components/Text'
import { useTranslation } from '@common/config/localization'
import usePrevious from '@common/hooks/usePrevious'
import useToast from '@common/hooks/useToast'
import spacings from '@common/styles/spacings'
import flexbox from '@common/styles/utils/flexbox'
import { translateControllerMessage } from '@common/utils/translateControllerMessage'
import useBackgroundService from '@web/hooks/useBackgroundService'
import HardwareWalletSigningModal from '@web/modules/hardware-wallet/components/HardwareWalletSigningModal'
import { getUiType } from '@web/utils/uiType'

const { isPopup } = getUiType()

interface Props {
  signingKeyType?: AccountOp['signingKeyType']
  feePayerKeyType?: Key['type']
  isSignAndBroadcastInProgress: boolean
  signAccountOpStatusType?: SigningStatus
  shouldSignAuth: {
    type: 'V2Deploy' | '7702'
    text: string
  } | null
  signedTransactionsCount?: number | null
  accountOp: AccountOp
  actionType?: 'swapAndBridge' | 'transfer'
}

const SignAccountOpHardwareWalletSigningModal: React.FC<Props> = ({
  signingKeyType,
  feePayerKeyType,
  isSignAndBroadcastInProgress,
  signAccountOpStatusType,
  shouldSignAuth,
  signedTransactionsCount,
  accountOp,
  actionType
}: Props) => {
  const { t } = useTranslation()
  const { dispatch } = useBackgroundService()
  const { addToast } = useToast()

  const prevTransactionCount = usePrevious(signedTransactionsCount)

  const shouldBeVisible = useMemo(() => {
    // we're not signing or broadcasting on paused updates
    if (signAccountOpStatusType === SigningStatus.UpdatesPaused) return false

    const isCurrentlyBroadcastingWithExternalKey =
      isSignAndBroadcastInProgress && !!feePayerKeyType && feePayerKeyType !== 'internal'
    const isCurrentlySigningWithExternalKey =
      signAccountOpStatusType === SigningStatus.InProgress &&
      !!signingKeyType &&
      signingKeyType !== 'internal'

    return isCurrentlyBroadcastingWithExternalKey || isCurrentlySigningWithExternalKey
  }, [isSignAndBroadcastInProgress, feePayerKeyType, signAccountOpStatusType, signingKeyType])

  const currentlyInvolvedSignOrBroadcastKeyType = useMemo(
    () => (signAccountOpStatusType === SigningStatus.InProgress ? signingKeyType : feePayerKeyType),
    [feePayerKeyType, signAccountOpStatusType, signingKeyType]
  )

  useEffect(() => {
    const isSigningOneOfMultipleAsEoa = typeof signedTransactionsCount === 'number'

    if (!isSigningOneOfMultipleAsEoa) return

    const hasJustSigned = signedTransactionsCount > (prevTransactionCount || 0)

    if (hasJustSigned) {
      const hasSignedLast = signedTransactionsCount === accountOp?.calls.length

      const toastValues = { signed: signedTransactionsCount, total: accountOp?.calls.length }
      addToast(
        hasSignedLast
          ? t('Transaction {{signed}} / {{total}} signed! Broadcasting...', toastValues)
          : t('Transaction {{signed}} / {{total}} signed! Sending next...', toastValues)
      )
    }
  }, [accountOp?.calls.length, addToast, prevTransactionCount, signedTransactionsCount, t])

  useEffect(() => {
    if (
      shouldBeVisible &&
      actionType &&
      isPopup &&
      currentlyInvolvedSignOrBroadcastKeyType === 'trezor'
    ) {
      // If the user needs to sign using a hardware wallet, we need to open the
      // screen in an request window and close the popup
      dispatch({
        type: 'OPEN_SIGNING_REQUEST_WINDOW',
        params: { type: actionType }
      })
      window.close()
    }
  }, [actionType, currentlyInvolvedSignOrBroadcastKeyType, dispatch, shouldBeVisible])

  const handleBackdropPress = useCallback(() => {
    const updateType =
      actionType === 'swapAndBridge'
        ? 'Swap&Bridge'
        : actionType === 'transfer'
          ? 'Transfer&TopUp'
          : 'Requests'

    dispatch({
      type: 'CURRENT_SIGN_ACCOUNT_OP_UPDATE_STATUS',
      params: { updateType, status: SigningStatus.ReadyToSign }
    })
  }, [actionType, dispatch])

  if (!currentlyInvolvedSignOrBroadcastKeyType) return null

  return (
    <HardwareWalletSigningModal
      isVisible={shouldBeVisible}
      keyType={currentlyInvolvedSignOrBroadcastKeyType as any}
      onBackdropPress={handleBackdropPress}
    >
      {typeof signedTransactionsCount === 'number' ? (
        <View style={[flexbox.alignCenter, flexbox.justifyCenter, spacings.ptLg]}>
          <Text weight="medium" fontSize={16}>
            {t('{{count}} / {{total}} transactions signed', {
              count: signedTransactionsCount,
              total: accountOp?.calls.length
            })}
          </Text>
        </View>
      ) : null}

      {shouldSignAuth ? (
        <View style={[flexbox.alignCenter, flexbox.justifyCenter, spacings.ptLg]}>
          <Text weight="medium" fontSize={16}>
            {translateControllerMessage(shouldSignAuth.text)}
          </Text>
        </View>
      ) : null}
    </HardwareWalletSigningModal>
  )
}

export default React.memo(SignAccountOpHardwareWalletSigningModal)
